import { Controller } from "@hotwired/stimulus";

const PDF_CREATION_DATE = [
  /CreationDate\s*\(D:(\d{4})(\d{2})(\d{2})/,
  /xmp:CreateDate(?:>|=")(\d{4})-(\d{2})-(\d{2})/,
];
const PDF_METADATA_SCAN_BYTES = 1024 * 1024;

export default class extends Controller {
  static targets = [
    "input",
    "template",
    "list",
    "review",
    "count",
    "submit",
    "rejections",
    "row",
  ];
  static values = { maxSize: Number };

  add() {
    const files = Array.from(this.inputTarget.files);
    const accepted = files.filter((file) => this.#acceptable(file));

    accepted.forEach((file) => this.#addRow(file));
    this.#showRejections(files.filter((file) => !accepted.includes(file)));
    this.inputTarget.value = "";
  }

  rowTargetConnected() {
    this.refresh();
  }

  rowTargetDisconnected() {
    this.refresh();
  }

  refresh() {
    const states = this.rowTargets.map((row) => row.dataset.state);
    const noun = states.length === 1 ? "document" : "documents";

    this.reviewTarget.hidden = states.length === 0;
    this.countTarget.textContent = `${states.length} ${noun} to add`;
    this.submitTarget.disabled =
      states.length === 0 || states.some((state) => state !== "uploaded");

    if (states.includes("failed")) {
      this.submitTarget.textContent = "Remove failed files to save";
    } else if (states.includes("uploading")) {
      this.submitTarget.textContent = "Uploading…";
    } else {
      this.submitTarget.textContent = `Save ${states.length} ${noun}`;
    }
  }

  #acceptable(file) {
    const accepted = this.inputTarget.accept.split(",");
    const extension = `.${file.name.split(".").pop().toLowerCase()}`;
    return file.size <= this.maxSizeValue && accepted.includes(extension);
  }

  async #addRow(file) {
    const row = this.templateTarget.content.firstElementChild.cloneNode(true);
    const field = (name) => row.querySelector(`[data-field=${name}]`);
    const transfer = new DataTransfer();

    transfer.items.add(file);
    field("file").files = transfer.files;
    field("filename").textContent = file.name;
    field("type").textContent = file.name.includes(".")
      ? file.name.split(".").pop().toLowerCase()
      : "";
    field("name").value = file.name.replace(/\.[^.]+$/, "");
    field("date").value = this.#isoDate(new Date(file.lastModified));
    this.listTarget.append(row);

    const createdOn = await this.#pdfCreationDate(file).catch(() => null);
    if (createdOn) field("date").value = createdOn;
  }

  async #pdfCreationDate(file) {
    if (file.type !== "application/pdf") return null;

    const text =
      (await this.#text(file.slice(0, PDF_METADATA_SCAN_BYTES))) +
      (await this.#text(file.slice(-PDF_METADATA_SCAN_BYTES)));
    const match = PDF_CREATION_DATE.map((pattern) => text.match(pattern)).find(
      Boolean,
    );
    return match && Number(match[1]) > 1900
      ? `${match[1]}-${match[2]}-${match[3]}`
      : null;
  }

  async #text(blob) {
    return new TextDecoder("latin1").decode(await blob.arrayBuffer());
  }

  #isoDate(date) {
    const pad = (number) => String(number).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  #showRejections(files) {
    this.rejectionsTarget.hidden = files.length === 0;
    this.rejectionsTarget.textContent = files.length
      ? `Skipped ${files.map((file) => file.name).join(", ")}: only PDFs, images, Word, Excel, text, and CSV files up to ${Math.round(this.maxSizeValue / 1024 / 1024)} MB can be added.`
      : "";
  }
}
